import { PencilIcon } from '@heroicons/react/24/outline';
import { Flex, Space, Stack, Tabs, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import React from 'react';
import { FormattedTime } from 'react-intl';

import { PageLoading } from '@/pages/PageLoading';
import { useSchedulePage } from '@/pages/club/onboard/components/stages/schedule/hooks/useSchedulePage';
import { translate } from '@/shared/i18n/translate';
import { LBModalLayout } from '@/shared/layouts';
import { LBDailyTabs } from '@/shared/modules';
import {
  LBButton,
  LBCheckbox,
  LBLinkButton,
  LBListItem,
  LBTimePicker
} from '@/shared/ui-kit/components';

export const Schedule = () => {
  const {
    state: { isLoading, form, modal, editableDay },
    functions: { setEditableDay, onSubmit, setFullWeekData }
  } = useSchedulePage();
  const [fullModalState, { open: fullModalOpen, close: fullModalClose }] =
    useDisclosure();

  const { schedule, additional } = form.getValues();

  const daysOrder = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const sortedData = React.useMemo(
    () =>
      [...(schedule ?? [])].sort(
        (a, b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day)
      ),
    [schedule]
  );

  if (isLoading) return <PageLoading />;

  return (
    <>
      {/* Daily Edit Modal */}
      <LBModalLayout opened={modal.opened} onClose={modal.close}>
        <Title order={2}>
          {translate('pages.onboarding.club.schedule.popups.title')}
        </Title>
        <Space h={32} />
        <Tabs defaultValue={editableDay}>
          <LBDailyTabs.List>
            {sortedData.map((item) => (
              <LBDailyTabs.Tab key={item.day} value={item.day}>
                <Text>{item.day.slice(0, 1)}</Text>
              </LBDailyTabs.Tab>
            ))}
          </LBDailyTabs.List>
          <Space h={16} />
          {sortedData.map((item) => {
            const realIndex = schedule.findIndex((s) => s.day === item.day);

            return (
              <LBDailyTabs.Panel key={item.day} value={item.day}>
                <Stack gap={8}>
                  <LBCheckbox
                    key={form.key(`schedule.${realIndex}.isOpen24Hours`)}
                    checked={item.isOpen24Hours}
                    label={translate('commons.aroundTheClock')}
                    onChange={(e) => {
                      const value = e.currentTarget.checked;
                      form.setFieldValue(
                        `schedule.${realIndex}.isOpen24Hours`,
                        value
                      );
                      if (value) {
                        form.setFieldValue(
                          `schedule.${realIndex}.isClosed`,
                          false
                        );
                      }
                    }}
                  />
                  <LBCheckbox
                    key={form.key(`schedule.${realIndex}.isClosed`)}
                    checked={item.isClosed}
                    label={translate('commons.closed')}
                    onChange={(e) => {
                      const value = e.currentTarget.checked;
                      form.setFieldValue(
                        `schedule.${realIndex}.isClosed`,
                        value
                      );
                      if (value) {
                        form.setFieldValue(
                          `schedule.${realIndex}.isOpen24Hours`,
                          false
                        );
                      }
                    }}
                  />
                </Stack>
                <Space h={16} />
                <Flex gap={16} justify='space-between'>
                  <LBTimePicker
                    key={form.key(`schedule.${realIndex}.startHour`)}
                    disabled={item.isClosed || item.isOpen24Hours}
                    label={translate('form.workTime.start.label')}
                    {...form.getInputProps(`schedule.${realIndex}.startHour`)}
                  />
                  <LBTimePicker
                    key={form.key(`schedule.${realIndex}.closeHour`)}
                    disabled={item.isClosed || item.isOpen24Hours}
                    label={translate('form.workTime.end.label')}
                    {...form.getInputProps(`schedule.${realIndex}.closeHour`)}
                  />
                </Flex>
              </LBDailyTabs.Panel>
            );
          })}
        </Tabs>
        <Space h={32} />
        <Flex gap={16} justify='end'>
          <LBLinkButton onClick={modal.close}>
            {translate('commons.cancel')}
          </LBLinkButton>
          <LBLinkButton onClick={modal.close}>
            {translate('commons.save')}
          </LBLinkButton>
        </Flex>
      </LBModalLayout>

      {/* Full Week Modal */}
      <LBModalLayout opened={fullModalState} onClose={fullModalClose}>
        <Title order={2}>
          {translate('pages.onboarding.club.schedule.popups.title')}
        </Title>
        <Space h={32} />
        <Tabs defaultValue='fullWeek'>
          <LBDailyTabs.List>
            {sortedData.map((item) => (
              <LBDailyTabs.Tab key={item.day} disabled value={item.day}>
                <Text>{item.day[0]}</Text>
              </LBDailyTabs.Tab>
            ))}
          </LBDailyTabs.List>
          <Space h={16} />
          <LBDailyTabs.Panel value='fullWeek'>
            <Stack gap={8}>
              <LBCheckbox
                key={form.key(`additional.isOpen24Hours`)}
                checked={additional.isOpen24Hours}
                label={translate('commons.aroundTheClock')}
                onChange={(e) => {
                  const value = e.currentTarget.checked;
                  form.setFieldValue(`additional.isOpen24Hours`, value);
                }}
              />
            </Stack>
            <Space h={16} />
            <Flex gap={16} justify='space-between'>
              <LBTimePicker
                key={form.key(`additional.startHour`)}
                disabled={additional.isOpen24Hours}
                label={translate('form.workTime.start.label')}
                {...form.getInputProps(`additional.startHour`)}
              />
              <LBTimePicker
                key={form.key(`additional.closeHour`)}
                disabled={additional.isOpen24Hours}
                label={translate('form.workTime.end.label')}
                {...form.getInputProps(`additional.closeHour`)}
              />
            </Flex>
          </LBDailyTabs.Panel>
        </Tabs>
        <Space h={32} />
        <Flex gap={16} justify='end'>
          <LBLinkButton onClick={fullModalClose}>
            {translate('commons.cancel')}
          </LBLinkButton>
          <LBLinkButton
            onClick={() => {
              setFullWeekData();
              fullModalClose();
            }}
          >
            {translate('commons.save')}
          </LBLinkButton>
        </Flex>
      </LBModalLayout>

      {/* Main Schedule View */}
      <form onSubmit={onSubmit}>
        <Title order={1}>
          {translate('pages.onboarding.club.schedule.title')}
        </Title>
        <Space h={32} />
        <Stack gap={8}>
          {sortedData.map((item) => (
            <LBListItem key={item.day}>
              <Text fz={14}>{translate(`commons.schedule.${item.day}`)}</Text>
              <LBLinkButton
                disabled={form.submitting}
                rightsection={<PencilIcon width={18} />}
                type='button'
                onClick={() => {
                  setEditableDay(item.day);
                  modal.open();
                }}
              >
                {item.isClosed || item.isOpen24Hours ? (
                  <Text fz={14}>
                    {item.isClosed
                      ? translate('commons.closed')
                      : translate('commons.aroundTheClock')}
                  </Text>
                ) : (
                  <Text fz={14}>
                    <FormattedTime
                      value={new Date().setHours(
                        +item.startHour.slice(0, 2),
                        +item.startHour.slice(3, 5)
                      )}
                    />
                    -
                    <FormattedTime
                      value={new Date().setHours(
                        +item.closeHour.slice(0, 2),
                        +item.closeHour.slice(3, 5)
                      )}
                    />
                  </Text>
                )}
              </LBLinkButton>
            </LBListItem>
          ))}
        </Stack>
        <Space h={24} />
        <Flex justify='end'>
          <LBLinkButton
            disabled={form.submitting}
            type='button'
            onClick={fullModalOpen}
          >
            {translate('pages.onboarding.club.schedule.changeAllWorkTimes')}
          </LBLinkButton>
        </Flex>
        <Space h={32} />
        <LBButton fullWidth loading={form.submitting} type='submit'>
          {translate('commons.save-and-continue')}
        </LBButton>
      </form>
    </>
  );
};
